import React, { useState, useEffect, useCallback } from 'react';
import { fetchPositions, exitPositions } from '../api';

const REFRESH_INTERVAL = 10000;

export default function Positions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [exiting, setExiting] = useState(false);
  const [filterSource, setFilterSource] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await fetchPositions();
      if (res.data?.status === 'ok') setData(res.data);
    } catch {}
    setLoading(false);
    setLastRefresh(new Date().toLocaleTimeString());
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  const toggleSelect = (posId) => {
    setSelected(prev => ({ ...prev, [posId]: !prev[posId] }));
  };

  const selectAll = () => {
    const filtered = getFilteredPositions();
    const allSelected = filtered.every(p => selected[p.id]);
    const next = { ...selected };
    filtered.forEach(p => { next[p.id] = !allSelected; });
    setSelected(next);
  };

  const getFilteredPositions = () => {
    if (!data?.positions) return [];
    return data.positions.filter(p =>
      (!filterSource || p.source === filterSource) &&
      (!filterAccount || p.account_name === filterAccount)
    );
  };

  const handleExit = async () => {
    const toExit = getFilteredPositions().filter(p => selected[p.id]);
    if (toExit.length === 0) return;
    // Deduplicate by strategy_name so we don't exit the same strategy twice
    const seen = new Set();
    const payload = [];
    for (const p of toExit) {
      const key = `${p.strategy_name}::${p.position_index ?? -1}`;
      if (!seen.has(key)) {
        seen.add(key);
        payload.push({
          strategy_name: p.strategy_name,
          position_index: p.position_index !== undefined && p.position_index !== null ? p.position_index : -1,
        });
      }
    }
    if (!window.confirm(`Exit ${payload.length} position(s)?`)) return;

    setExiting(true);
    try {
      await exitPositions({ positions: payload });
      setSelected({});
      await loadData();
    } catch {}
    setExiting(false);
  };

  const positions = getFilteredPositions();
  const selectedCount = positions.filter(p => selected[p.id]).length;
  const accounts = data?.accounts || [];
  const accountNames = [...new Set((data?.positions || []).map(p => p.account_name))];
  const totalPnl = positions.reduce((s, p) => s + (p.pnl_amount || 0), 0);
  const brokerDayPnl = data?.broker_day_pnl || null;

  const formatAmt = (v) => {
    const sign = v >= 0 ? '+' : '-';
    const abs = Math.abs(v);
    if (abs >= 100000) return sign + '\u20B9' + (abs / 100000).toFixed(2) + 'L';
    return sign + '\u20B9' + abs.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  if (loading && !data) {
    return <div className="loading"><div className="spinner" /><p>Loading positions...</p></div>;
  }

  return (
    <div className="page-positions">
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Open Positions</h1>
          <p className="page-subtitle">All open positions across accounts — individual legs</p>
        </div>
        {lastRefresh && (
          <span className="live-indicator">
            <span className="live-pulse" /> Updated {lastRefresh}
          </span>
        )}
      </div>

      {/* Summary Bar */}
      <section className="sl-stats-bar">
        <div className="sl-stat-item">
          <span className="sl-stat-label">Total Legs</span>
          <span className="sl-stat-value">{positions.length}</span>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Selected</span>
          <span className="sl-stat-value sl-active-val">{selectedCount}</span>
        </div>
        {brokerDayPnl && (
          <>
            <div className="sl-stat-item">
              <span className="sl-stat-label">Day P&amp;L (Broker)</span>
              <span className={`sl-stat-value ${brokerDayPnl.total >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: '22px' }}>
                {formatAmt(brokerDayPnl.total)}
              </span>
            </div>
            <div className="sl-stat-item">
              <span className="sl-stat-label">Realised</span>
              <span className={`sl-stat-value ${brokerDayPnl.realised >= 0 ? 'positive' : 'negative'}`}>
                {formatAmt(brokerDayPnl.realised)}
              </span>
            </div>
            <div className="sl-stat-item">
              <span className="sl-stat-label">Unrealised</span>
              <span className={`sl-stat-value ${brokerDayPnl.unrealised >= 0 ? 'positive' : 'negative'}`}>
                {formatAmt(brokerDayPnl.unrealised)}
              </span>
            </div>
          </>
        )}
        {!brokerDayPnl && (
          <div className="sl-stat-item">
            <span className="sl-stat-label">Open P&amp;L</span>
            <span className={`sl-stat-value ${totalPnl >= 0 ? 'positive' : 'negative'}`}>
              {formatAmt(totalPnl)}
            </span>
          </div>
        )}
        <div className="sl-stat-item">
          <button
            className="btn btn-danger"
            onClick={handleExit}
            disabled={exiting || selectedCount === 0}
            style={{ padding: '8px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '8px', cursor: selectedCount > 0 ? 'pointer' : 'not-allowed', background: selectedCount > 0 ? '#ef4444' : '#555', color: '#fff', border: 'none' }}
          >
            {exiting ? 'Exiting...' : `Exit Selected (${selectedCount})`}
          </button>
        </div>
      </section>

      {/* Filters */}
      <div className="inst-filters">
        <select value={filterSource} onChange={e => setFilterSource(e.target.value)}>
          <option value="">All Sources</option>
          <option value="system">System</option>
          <option value="manual">Manual</option>
          <option value="user">User / Broker</option>
        </select>
        <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
          <option value="">All Accounts</option>
          {accountNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button className="dash-view-all" onClick={selectAll} style={{ marginLeft: 'auto' }}>
          {positions.every(p => selected[p.id]) && positions.length > 0 ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {/* Account Groups */}
      {accounts.map(acct => {
        const acctPositions = positions.filter(p => p.account_name === acct.account_name);
        if (acctPositions.length === 0) return null;
        return (
          <section key={acct.account_name} className="sl-group">
            <h2 className="sl-group-title">
              <span className="sl-group-icon">👤</span> {acct.account_name}
              {acct.broker_day_pnl ? (
                <span style={{ fontSize: '14px', marginLeft: '12px' }}>
                  <span style={{ color: acct.broker_day_pnl.total >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                    Day: {formatAmt(acct.broker_day_pnl.total)}
                  </span>
                  <span style={{ color: '#888', marginLeft: '8px', fontSize: '12px' }}>
                    (R: {formatAmt(acct.broker_day_pnl.realised)} | U: {formatAmt(acct.broker_day_pnl.unrealised)})
                  </span>
                </span>
              ) : (
                <span style={{ fontSize: '14px', marginLeft: '12px', color: acct.total_pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                  {formatAmt(acct.total_pnl)}
                </span>
              )}
            </h2>
            <div className="table-wrapper">
              <table className="trade-table-v2">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={acctPositions.length > 0 && acctPositions.every(p => selected[p.id])}
                        onChange={() => {
                          const allSel = acctPositions.every(p => selected[p.id]);
                          const next = { ...selected };
                          acctPositions.forEach(p => { next[p.id] = !allSel; });
                          setSelected(next);
                        }}
                      />
                    </th>
                    <th>Source</th>
                    <th>Strategy</th>
                    <th>Index</th>
                    <th>Type</th>
                    <th>Strike</th>
                    <th>Side</th>
                    <th>Entry Price</th>
                    <th>Current</th>
                    <th>P&amp;L (pts)</th>
                    <th>P&amp;L (₹)</th>
                    <th>Entry</th>
                    <th>Exit</th>
                  </tr>
                </thead>
                <tbody>
                  {acctPositions.map(pos => (
                    <tr key={pos.id} className={selected[pos.id] ? 'selected-row' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selected[pos.id]}
                          onChange={() => toggleSelect(pos.id)}
                        />
                      </td>
                      <td>
                        <span className={`source-badge ${pos.source}`}>
                          {pos.source === 'system' ? '🤖 System' : pos.source === 'user' ? '👤 User' : '✋ Manual'}
                        </span>
                      </td>
                      <td>
                        <span className="strat-name-cell">
                          {pos.entry_type === 'opening_range' ? 'Opening Range' :
                           pos.entry_type === 'atm_straddle' ? 'ATM Straddle' :
                           pos.entry_type === 'rolling_strangle' ? 'Rolling Strangle' :
                           pos.entry_type === 'manual_single_leg' ? 'Manual' :
                           pos.entry_type === 'broker' ? 'Broker' : pos.entry_type}
                        </span>
                        {pos.paper_trading && <span className="chip-paper" style={{ marginLeft: 4, fontSize: 10 }}>Paper</span>}
                      </td>
                      <td>{pos.index}</td>
                      <td>
                        <span className={pos.option_type === 'CE' ? 'ce-type' : 'pe-type'} style={{ fontWeight: 700 }}>
                          {pos.option_type}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{pos.strike}</td>
                      <td>
                        <span style={{ color: pos.side === 'SELL' ? '#f85149' : '#56d364', fontWeight: 600, fontSize: 12 }}>
                          {pos.side || 'SELL'}
                        </span>
                      </td>
                      <td>{(pos.sell_price || pos.buy_price || 0).toFixed(2)}</td>
                      <td>{(pos.current_price || 0).toFixed(2)}</td>
                      <td className={pos.pnl_points >= 0 ? 'positive' : 'negative'}>
                        {pos.pnl_points >= 0 ? '+' : ''}{pos.pnl_points.toFixed(2)}
                      </td>
                      <td className={pos.pnl_amount >= 0 ? 'positive' : 'negative'} style={{ fontWeight: 600 }}>
                        {formatAmt(pos.pnl_amount)}
                      </td>
                      <td className="trade-time">{pos.entry_time}</td>
                      <td>
                        <button
                          className="btn-exit-small"
                          onClick={async () => {
                            if (!window.confirm(`Exit this ${pos.entry_type} position?`)) return;
                            setExiting(true);
                            try {
                              await exitPositions({
                                positions: [{
                                  strategy_name: pos.strategy_name,
                                  position_index: pos.position_index !== undefined && pos.position_index !== null ? pos.position_index : -1,
                                }],
                              });
                              await loadData();
                            } catch {}
                            setExiting(false);
                          }}
                          disabled={exiting}
                          style={{ padding: '4px 12px', fontSize: '12px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                        >
                          Exit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      {positions.length === 0 && (
        <div className="dash-empty">No open positions</div>
      )}
    </div>
  );
}
